/**
 * Logic Editor Tab for inspecting and editing mapping transformations.
 * Composes BindingEditor, ParametersEditor, and VisualMappingBuilder sub-components.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControlLabel,
  MenuItem,
  Select,
  Slider,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import {
  type FieldMapping,
  type HealthIssue,
  type MappingResponse,
  type TransformationStep,
} from '../../services/api';
import type { CanvasSelection } from '../types';
import VisualMappingBuilder from '../visualPipeline/VisualMappingBuilder';
import { isTrueLiteralMapping } from '../utils/mappingTypeUtils';
import MappingCreationPanel from '../MappingCreationPanel';
import { useExpressionValidation } from '../hooks/useExpressionValidation';
import type { SuggestedFix } from '../validation/typeCompatibility';
import { clampConfidence } from '../../utils/dataUtils';
import type { TransformationFormValues } from './types';
import { OPERATION_TYPES, emptyForm } from './constants';
import ParametersEditor from './ParametersEditor';
import BindingEditor from './BindingEditor';
import { extractSourceOptions, extractTargetOptions } from './schemaOptionUtils';

const VALUE_ONLY_OPERATIONS = new Set([
  'set_constant',
  'set_null',
  'constant',
  'literal',
  'generate_uuid',
]);

function buildInitialFormFromMapping(
  mapping: FieldMapping,
  activeStep: TransformationStep | undefined,
): TransformationFormValues {
  const resolvedOperation =
    mapping.operation || activeStep?.operation || mapping.transformation_type || 'expression';
  return {
    transformation: mapping.transformation ?? '',
    explanation: mapping.explanation ?? '',
    confidence: clampConfidence(mapping.confidence ?? null),
    isRequired: Boolean(mapping.is_required),
    defaultValue: mapping.default_value ? JSON.stringify(mapping.default_value, null, 0) : '',
    removeTransformation: !mapping.transformation && !VALUE_ONLY_OPERATIONS.has(resolvedOperation),
    stepDescription: activeStep?.description ?? '',
    operation: resolvedOperation,
    parameters: mapping.parameters || activeStep?.parameters,
    aiGenerated: Boolean(activeStep?.ai_generated),
  };
}

export interface LogicEditorTabProps {
  selection: CanvasSelection | null;
  mappingResponse?: MappingResponse;
  onSave?: (mapping: FieldMapping, values: TransformationFormValues, activeStep: TransformationStep | undefined) => void;
  onCreate?: (targetTable: string, targetField: string, sourceFile?: string, sourceField?: string, transformation?: string, transformationType?: string, operation?: string, parameters?: Record<string, unknown>) => void;
  onRegenerate?: (mapping: FieldMapping, context: string) => void;
  onValidate?: (expression: string) => void;
  onAskAboutMapping?: (fieldLabel: string) => void;
  onDelete?: (mapping: FieldMapping) => void;
  saving?: boolean;
  regenerating?: boolean;
  validating?: boolean;
  deleting?: boolean;
  healthIssues?: HealthIssue[];
}

export const LogicEditorTab: React.FC<LogicEditorTabProps> = ({
  selection,
  mappingResponse,
  onSave,
  onCreate,
  onRegenerate: _onRegenerate,
  onValidate,
  onAskAboutMapping,
  onDelete,
  saving,
  regenerating: _regenerating,
  validating,
  deleting,
  healthIssues,
}) => {
  // ── Derive selected mapping and step ──────────────────────────────

  const [mapping, activeStep, targetInfo] = useMemo(() => {
    if (!selection) return [undefined, undefined, undefined] as const;

    const mappingId = selection.kind === 'node'
      ? selection.node.data.mapping?.mapping_id ?? selection.node.data.mappingIds?.[0]
      : selection.edge.data?.mappingId;

    if (mappingId && mappingResponse) {
      const foundMapping = mappingResponse.mappings?.find((m) => m.mapping_id === mappingId);
      if (foundMapping) {
        let step: TransformationStep | undefined;
        if (selection.kind === 'node' && selection.node.data.step) {
          step = selection.node.data.step;
        } else if (selection.kind === 'edge' && selection.edge.data?.stepId) {
          step = foundMapping.transformation_steps?.find((s) => s.step_id === selection.edge.data?.stepId);
        } else {
          step = foundMapping.transformation_steps?.[0];
        }
        return [foundMapping, step, undefined] as const;
      }
    }

    if (selection.kind === 'node' && selection.node.data.kind === 'targetField') {
      const targetTable = selection.node.data.table;
      const targetField = selection.node.data.field;
      if (targetTable && targetField) {
        return [undefined, undefined, { targetTable, targetField }] as const;
      }
    }

    return [undefined, undefined, undefined] as const;
  }, [selection, mappingResponse]);

  // ── Form state ────────────────────────────────────────────────────

  const [formState, setFormState] = useState<TransformationFormValues>(emptyForm);
  const [bindingOverrides, setBindingOverrides] = useState<Partial<TransformationFormValues>>({});
  const [editedSourceFields, setEditedSourceFields] = useState<string[]>([]);

  useEffect(() => {
    if (!mapping) {
      setFormState(emptyForm);
      return;
    }
    setFormState(buildInitialFormFromMapping(mapping, activeStep));
    setBindingOverrides({});
  }, [mapping, activeStep]);

  const handleChange = useCallback((key: keyof TransformationFormValues, value: unknown) => {
    setFormState((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleSave = () => {
    if (onSave && mapping) {
      onSave(mapping, { ...formState, ...bindingOverrides }, activeStep);
    }
  };

  // ── Schema options ────────────────────────────────────────────────

  const sourceOptions = useMemo(() => extractSourceOptions(mappingResponse), [mappingResponse]);
  const targetOptions = useMemo(() => extractTargetOptions(mappingResponse), [mappingResponse]);
  const isValueOnlyOperation =
    !formState.removeTransformation &&
    VALUE_ONLY_OPERATIONS.has(formState.operation || '');
  const isConstantOperation = !formState.removeTransformation && formState.operation === 'set_constant';
  const constantValue = String((formState.parameters as Record<string, unknown> | undefined)?.value ?? '').trim();
  const isMissingConstantValue = isConstantOperation && constantValue.length === 0;

  // ── Expression validation ─────────────────────────────────────────

  const { result: exprValidation, isValidating: exprValidating, validate: triggerExprValidation } = useExpressionValidation({
    debounceMs: 500,
    sourceFile: bindingOverrides.sourceFile || mapping?.source_file,
  });

  useEffect(() => {
    if (formState.transformation && !formState.removeTransformation) {
      triggerExprValidation(formState.transformation);
    }
  }, [formState.transformation, formState.removeTransformation, triggerExprValidation]);

  const handleExpressionChange = useCallback(
    (expr: string) => {
      setFormState((prev) => ({ ...prev, transformation: expr }));
    },
    []
  );

  const handleApplyTypeFix = useCallback((fix: SuggestedFix) => {
    handleChange('transformation', fix.expression);
    handleChange('removeTransformation', false);
    if (fix.fixType === 'cast' || fix.fixType === 'coalesce' || fix.fixType === 'substring' || fix.fixType === 'round') {
      handleChange('operation', 'expression');
    }
  }, [handleChange]);

  const handleValidate = () => {
    if (onValidate && formState.transformation) {
      onValidate(formState.transformation);
    }
  };

  // ── Source selection state for unmapped target fields ──────────────

  const [selectedSourceFile, setSelectedSourceFile] = useState<string | null>(null);
  const [selectedSourceField, setSelectedSourceField] = useState<string | null>(null);

  useEffect(() => {
    setSelectedSourceFile(null);
    setSelectedSourceField(null);
  }, [targetInfo?.targetTable, targetInfo?.targetField]);

  // ── Early returns (after all hooks) ───────────────────────────────

  if (!mapping) {
    if (targetInfo) {
      return (
        <MappingCreationPanel
          targetTable={targetInfo.targetTable}
          targetField={targetInfo.targetField}
          mappingResponse={mappingResponse}
          sourceOptions={sourceOptions}
          onCreate={(tt, tf, sf, sfield, transformation, transformationType, operation, parameters) => {
            if (onCreate) onCreate(tt, tf, sf, sfield, transformation, transformationType, operation, parameters);
          }}
          saving={saving}
        />
      );
    }
    return (
      <Box p={4} textAlign="center">
        <Typography variant="body2" color="text.secondary">Select a mapped field to edit logic.</Typography>
      </Box>
    );
  }

  // ── Main render ───────────────────────────────────────────────────

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Stack spacing={1.5} p={2} sx={{ flex: 1, overflow: 'auto' }}>

        {/* Expression failure alert (from preview) */}
        {healthIssues && (() => {
          const exprFailures = healthIssues.filter(
            (i) => i.category === 'expression_failure' &&
              i.target_table?.toLowerCase() === mapping.target_table?.toLowerCase() &&
              i.target_field?.toLowerCase() === mapping.target_field?.toLowerCase()
          );
          return exprFailures.length > 0 ? (
            <Alert severity="error" sx={{ py: 0.5, fontSize: 12 }}>
              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                Expression failed during preview
              </Typography>
              {exprFailures.map((f, i) => (
                <Typography key={i} variant="caption" display="block" sx={{ fontSize: 11, mt: 0.25 }}>
                  {f.message}
                </Typography>
              ))}
            </Alert>
          ) : null;
        })()}

        {/* Mapping Binding (re-pointing editor) */}
        <BindingEditor
          mapping={mapping}
          mappingResponse={mappingResponse}
          sourceOptions={sourceOptions}
          targetOptions={targetOptions}
          transformation={formState.transformation}
          transformationType={formState.operation}
          hideSourceSelection={isValueOnlyOperation}
          onBindingChange={setBindingOverrides}
          onApplyTypeFix={handleApplyTypeFix}
          onEditedSourceFieldsChange={setEditedSourceFields}
        />

        <Divider />

        {/* Direct mapping toggle */}
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={!formState.removeTransformation}
              onChange={(_, checked) => handleChange('removeTransformation', !checked)}
            />
          }
          label={
            <Typography variant="body2">
              {formState.removeTransformation ? 'Direct mapping (no transformation)' : 'Use transformation'}
            </Typography>
          }
        />

        {/* Operation Type selector */}
        {!formState.removeTransformation && (
          <Box>
            <Typography variant="caption" color="text.secondary" fontWeight={600}
              sx={{ textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.5, display: 'block' }}>
              Operation Type
            </Typography>
            <Select
              size="small"
              fullWidth
              value={formState.operation || 'expression'}
              onChange={(e) => {
                const newOp = e.target.value;
                handleChange('operation', newOp);
                handleChange('parameters', undefined);
              }}
              sx={{ fontSize: 12 }}
            >
              {OPERATION_TYPES.map((op) => (
                <MenuItem key={op.value} value={op.value} sx={{ fontSize: 12 }}>
                  {op.label}
                </MenuItem>
              ))}
            </Select>
          </Box>
        )}

        {/* Contextual parameters based on operation type */}
        {!formState.removeTransformation && formState.operation && (
          <ParametersEditor
            operation={formState.operation}
            parameters={(formState.parameters || {}) as Record<string, unknown>}
            onChange={(params) => handleChange('parameters', params)}
            availableFields={editedSourceFields}
          />
        )}
        {isMissingConstantValue && (
          <Alert severity="warning" sx={{ py: 0.5, fontSize: 12 }}>
            Constant Value is required for Set Constant mappings.
          </Alert>
        )}

        {/* Visual Mapping Builder */}
        {!formState.removeTransformation && !isValueOnlyOperation && (
          <Box>
            <VisualMappingBuilder
              mapping={mapping}
              activeStep={activeStep}
              mappingResponse={mappingResponse}
              onExpressionChange={handleExpressionChange}
            />
            {formState.transformation && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5, px: 0.5 }}>
                {exprValidating ? (
                  <>
                    <CircularProgress size={12} sx={{ color: 'text.disabled' }} />
                    <Typography variant="caption" sx={{ fontSize: 10, color: 'text.disabled' }}>
                      Validating...
                    </Typography>
                  </>
                ) : exprValidation?.valid ? (
                  <>
                    <CheckCircleOutlineIcon sx={{ fontSize: 14, color: '#10b981' }} />
                    <Typography variant="caption" sx={{ fontSize: 10, color: '#10b981', fontWeight: 500 }}>
                      Valid expression
                      {exprValidation.analysis?.output_type_hint && (
                        <Box component="span" sx={{ color: 'text.secondary', ml: 0.5 }}>
                          ({exprValidation.analysis.output_type_hint})
                        </Box>
                      )}
                    </Typography>
                  </>
                ) : exprValidation && !exprValidation.valid ? (
                  <>
                    <ErrorOutlineIcon sx={{ fontSize: 14, color: '#ef4444' }} />
                    <Typography variant="caption" sx={{ fontSize: 10, color: '#ef4444', fontWeight: 500 }}>
                      {exprValidation.error || 'Invalid expression'}
                    </Typography>
                  </>
                ) : null}
              </Box>
            )}
          </Box>
        )}

        {formState.removeTransformation && (
          <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            The source field value will be passed directly to the target without any transformation.
          </Typography>
        )}

        {/* Explanation */}
        <TextField
          label="Explanation"
          multiline
          minRows={1}
          maxRows={3}
          value={formState.explanation}
          onChange={(e) => handleChange('explanation', e.target.value)}
          fullWidth
          size="small"
          sx={{ '& textarea': { fontSize: '0.82rem' } }}
        />

        {/* Confidence */}
        <Stack direction="row" spacing={2} alignItems="center">
          <Typography variant="caption" sx={{ minWidth: 80 }}>
            Confidence: {typeof formState.confidence === 'number' ? Math.round(formState.confidence * 100) : 0}%
          </Typography>
          <Slider
            value={formState.confidence ?? 0}
            min={0} max={1} step={0.01}
            onChange={(_, val) => handleChange('confidence', val as number)}
            size="small"
            sx={{ flex: 1 }}
          />
        </Stack>

        {/* Validate button */}
        {onValidate && (
          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              size="small"
              startIcon={validating ? <CircularProgress size={14} /> : <CheckCircleIcon />}
              onClick={handleValidate}
              disabled={validating || !formState.transformation}
              sx={{ fontSize: '0.75rem', textTransform: 'none' }}
              color="success"
            >
              {validating ? 'Validating...' : 'Validate'}
            </Button>
          </Stack>
        )}

        {/* Ask AI — opens Chat tab with mapping context */}
        {onAskAboutMapping && (
          <Button
            size="small"
            variant="text"
            onClick={() => {
              const label = isTrueLiteralMapping(mapping.transformation_type, mapping.transformation || '')
                ? `${mapping.target_table}.${mapping.target_field}`
                : `${mapping.source_file}.${mapping.source_field} → ${mapping.target_table}.${mapping.target_field}`;
              onAskAboutMapping(label);
            }}
            sx={{ textTransform: 'none', fontSize: 12, alignSelf: 'flex-start' }}
          >
            Ask AI About This Mapping
          </Button>
        )}
      </Stack>

      {/* Pinned bottom bar: Save only — Delete moved to overflow menu in header */}
      <Stack
        direction="row"
        spacing={1}
        justifyContent="space-between"
        alignItems="center"
        sx={{
          px: 2,
          py: 1.5,
          borderTop: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          flexShrink: 0,
        }}
      >
        {onDelete && mapping && (
          <Tooltip title={saving ? 'Wait for save to finish' : 'Remove this mapping from the recipe'}>
            <span>
              <Button
                size="small"
                variant="contained"
                color="error"
                disabled={deleting || saving}
                onClick={() => onDelete(mapping)}
                startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : <DeleteOutlineIcon fontSize="small" />}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                Delete
              </Button>
            </span>
          </Tooltip>
        )}
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || isMissingConstantValue}
          size="small"
          sx={{ ml: 'auto' }}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </Stack>
    </Box>
  );
};

export default LogicEditorTab;